/**
 * PactViewerPage — the human-facing story layer for any module.
 *
 * Wraps the <PactViewer> component with:
 * - The gallery's chrome (header, nav, footer) via AppShell
 * - A controls bar (cross-app toggle, tone filter)
 * - Loading / error / empty states
 * - A shareable URL (?source=<app>&tone=<variant>)
 */
import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { PactViewer, usePactViewer } from '@avidtech6/pact-viewer';
import './PactViewerPage.css';

interface CrossAppVersion {
  app: string;
  url: string;
  label: string;
}

export default function PactViewerPage() {
  const { id = '' } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [crossAppVersions, setCrossAppVersions] = useState<CrossAppVersion[]>([]);
  const [crossAppError, setCrossAppError] = useState(false);

  const source = (searchParams.get('source') as 'canonical' | 'app' | 'url') || 'canonical';
  const tone = (searchParams.get('tone') as 'friendly' | 'designer' | 'technical' | 'marketing' | 'all') || 'all';

  useEffect(() => {
    // Try the bundled cross-app-registry first (faster, no CORS)
    fetch('/feed/cross-app-registry.json')
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((data) => {
        setCrossAppVersions(data.modules?.[id]?.apps || []);
        setCrossAppError(false);
      })
      .catch(() => {
        setCrossAppError(true);
        setCrossAppVersions([
          { app: 'canonical', url: `/feed/recipe/${id}/pact-viewer.yml`, label: 'Gallery canonical' },
        ]);
      });
  }, [id]);

  // Map source app name -> URL for the viewer
  const { data, loading, error } = usePactViewer(id, source);

  return (
    <div className="pact-viewer-page">
      <header className="pv-page-header">
        <div className="pv-breadcrumb">
          <Link to="/modules">← All modules</Link>
        </div>
        <h1 className="pv-page-title">{id || 'pact-viewer'}</h1>
        <p className="pv-page-subtitle">
          The human-facing story layer. Tone, personality, examples, and constraints
          — generated from the module's Recipe Book.
        </p>
      </header>

      <div className="pv-controls">
        <div className="pv-control-group">
          <label htmlFor="pv-source">How does each app use {id}?</label>
          <select
            id="pv-source"
            value={source}
            onChange={(e) => setSearchParams({ source: e.target.value })}
          >
            {crossAppVersions.map((v) => (
              <option key={v.app} value={v.app}>{v.label}</option>
            ))}
            {crossAppVersions.length === 0 && <option value="canonical">Gallery canonical</option>}
          </select>
          {crossAppError && (
            <span className="pv-control-hint">
              cross-app-registry.json missing on the live site. Showing only Gallery canonical.
            </span>
          )}
        </div>

        <div className="pv-control-group">
          <label htmlFor="pv-tone">Tone</label>
          <select
            id="pv-tone"
            value={tone}
            onChange={(e) => setSearchParams({ source, tone: e.target.value })}
          >
            <option value="all">All four tones</option>
            <option value="friendly">Friendly</option>
            <option value="designer">Designer</option>
            <option value="technical">Technical</option>
            <option value="marketing">Marketing</option>
          </select>
        </div>

        <div className="pv-control-group pv-control-group--right">
          <Link to={`/modules/${id}`} className="pv-control-link">
            Open the technical page →
          </Link>
        </div>
      </div>

      {loading && (
        <div className="pv-status">
          <div className="pv-spinner" />
          Loading {id} from <code>{source}</code>…
        </div>
      )}

      {error && (
        <div className="pv-status pv-status--error">
          <strong>Couldn't load the Pact data for <code>{id}</code></strong>
          <p>{error.message}</p>
          <p className="pv-status-hint">
            This usually means the yml file isn't bundled yet. The viewer falls back to
            reading the 8 source files of the module; if those don't exist either,
            the page stays empty.
          </p>
        </div>
      )}

      {!loading && !data && !error && (
        <div className="pv-status pv-status--empty">
          <strong>No Pact data for <code>{id}</code> yet</strong>
          <p>
            The pact-viewer.yml will appear here once it's generated (commit it to
            <code>feed/recipe/{id}/pact-viewer.yml</code>).
          </p>
        </div>
      )}

      {data && (
        <PactViewer
          moduleId={id}
          source={source}
          tone={tone}
          theme="dark"
        />
      )}
    </div>
  );
}
